import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FiBell } from 'react-icons/fi';
import { AuthContext } from '../auth/AuthContext';
import { markNotificationRead, markAllNotificationsRead } from '../api/notificationApi';
import { useNotificationFeed } from '../hooks/useNotificationFeed';
import { routeForNotification } from '../utils/notificationRoute';
import { COLORS as C, RADIUS } from '../constants/ui';

const PREVIEW_COUNT = 6;

const relativeTime = (iso) => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days < 7 ? `${days}d ago` : new Date(iso).toLocaleDateString();
};

/**
 * Always-visible unread count in the top bar, with a preview of the latest
 * items — the place people expect it, rather than a sidebar row that scrolls
 * out of view.
 *
 * Shares the sidebar's query key, so both badges come from one cached poll and
 * marking something read updates them together.
 */
export default function NotificationBell() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const { notifications, unread } = useNotificationFeed({ enabled: !!user?.id });
  const recent = notifications.slice(0, PREVIEW_COUNT);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['notifications'] });
  const markRead = useMutation({ mutationFn: markNotificationRead, onSuccess: invalidate });
  const markAll = useMutation({ mutationFn: markAllNotificationsRead, onSuccess: invalidate });

  // Close on outside click and on Escape — expected of any menu like this.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user?.id) return null;

  const openItem = (n) => {
    if (!n.is_read) markRead.mutate(n.id);
    setOpen(false);
    navigate(routeForNotification(n, user));
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 38,
          height: 38,
          borderRadius: RADIUS.control,
          border: `1px solid ${C.line}`,
          background: open ? C.accentSoft : '#fff',
          color: open ? C.accentInk : C.inkSoft,
          cursor: 'pointer',
        }}
      >
        <FiBell size={18} />
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -5,
              right: -5,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 999,
              background: C.danger,
              color: '#fff',
              fontSize: '0.65rem',
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #fff',
              boxSizing: 'content-box',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 'min(360px, calc(100vw - 2rem))',
            background: '#fff',
            border: `1px solid ${C.line}`,
            borderRadius: RADIUS.card,
            boxShadow: '0 18px 44px rgba(15,23,42,0.18)',
            zIndex: 1200,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '10px 12px',
              borderBottom: `1px solid ${C.line}`,
            }}
          >
            <strong style={{ fontSize: '0.85rem', color: C.ink }}>
              Notifications{unread > 0 ? ` (${unread})` : ''}
            </strong>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                style={{
                  background: 'none',
                  border: 'none',
                  color: C.accentInk,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            {recent.length === 0 ? (
              <div
                style={{
                  padding: '22px 12px',
                  textAlign: 'center',
                  color: C.inkFaint,
                  fontSize: '0.82rem',
                }}
              >
                Nothing new in the last 7 days.
              </div>
            ) : (
              recent.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  role="menuitem"
                  onClick={() => openItem(n)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    border: 'none',
                    borderBottom: `1px solid ${C.line}`,
                    background: n.is_read ? '#fff' : C.accentSoft,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: '0.83rem',
                        fontWeight: n.is_read ? 500 : 700,
                        color: C.ink,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {n.title}
                    </span>
                    <span style={{ fontSize: '0.68rem', color: C.inkFaint, flexShrink: 0 }}>
                      {relativeTime(n.created_at)}
                    </span>
                  </div>
                  {n.message && (
                    <div
                      style={{
                        fontSize: '0.76rem',
                        color: C.inkSoft,
                        marginTop: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {n.message}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
            style={{
              display: 'block',
              width: '100%',
              padding: '10px',
              border: 'none',
              background: C.sunken,
              color: C.accentInk,
              fontSize: '0.8rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}
